import React, { useEffect, useState, useCallback } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { api, Question, Variable, DataPoint, Dataset, PaginatedDataWrapper } from '../api/client';
import VariableManager from './VariableManager';
import DataPointManager from './DataPointManager';
import CorrelationView from './CorrelationView';
import EditQuestionModal from './EditQuestionModal';
import { formatShortDate } from '../utils/date';

const QuestionDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [question, setQuestion] = useState<Question | null>(null);
  const [variables, setVariables] = useState<Variable[]>([]);
  const [dataset, setDataset] = useState<Dataset | null>(null);
  const [datasetPagination, setDatasetPagination] = useState<PaginatedDataWrapper<Dataset> | null>(null);
  const [datasetPage, setDatasetPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'variables' | 'data' | 'correlation'>('data');
  const [showEditModal, setShowEditModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const loadDataset = useCallback(async (page = 1) => {
    if (!id) return;
    try {
      const ds = await api.listDataset(id, page);
      setDataset(ds.data);
      setDatasetPagination(ds);
      setDatasetPage(ds.page);
    } catch (err) {
      console.error('Failed to load dataset:', err);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      loadQuestion();
    }
  }, [id]);

  const loadQuestion = async () => {
    try {
      setLoading(true);
      const [questionData, variablesData, datasetData] = await Promise.all([
        api.getQuestion(id!),
        api.listVariables(id!),
        api.listDataset(id!, 1),
      ]);
      setQuestion(questionData);
      setVariables(variablesData);
      setDataset(datasetData.data);
      setDatasetPagination(datasetData);
      setDatasetPage(datasetData.page);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load question');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const sortedDataPoints = React.useMemo((): DataPoint[] => {
    if (!dataset) return [];
    return [...dataset.datapoints].sort(
      (a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
    );
  }, [dataset]);

  if (loading) return <div className="loading">Loading...</div>;
  if (error) return <div className="error">{error}</div>;
  if (!question) return <div className="error">Question not found</div>;

  const depVariables = variables.filter(v => v.dependent);
  const indepVariables = variables.filter(v => !v.dependent);
  const totalVariables = variables.length;
  const dataPointCount = datasetPagination?.total_count ?? sortedDataPoints.length;
  const lastRecordedAt = dataPointCount > 0 ? sortedDataPoints[0].created_at : null;
  const correlations = dataset?.correlations;
  const hasCorrelations = !!correlations && Object.values(correlations).some(
    row => Object.values(row).some(r => !r.error)
  );
  const allCorrelationsFailed = !!correlations && Object.keys(correlations).length > 0 && !hasCorrelations;
  const correlationReady = depVariables.length > 0 && indepVariables.length > 0 && dataPointCount > 0;

  const handleUpdateQuestion = (updated: Question) => {
    setQuestion(updated);
  };

  const handleDeleteQuestion = async () => {
    if (!window.confirm(`Are you sure you want to delete "${question.title}"? This action cannot be undone.`)) {
      return;
    }

    try {
      setDeleting(true);
      await api.deleteQuestion(id!);
      navigate('/');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete question');
      console.error(err);
      setDeleting(false);
    }
  };

  return (
    <div className="question-detail container">
      <div className="question-header">
        <Link to="/" className="back-link">← Back to Questions</Link>
        <div className="question-title-row">
          <h2>{question.title}</h2>
          <div className="button-group">
            <button
              onClick={() => setShowEditModal(true)}
              className="btn btn-secondary btn-sm"
              disabled={deleting}
            >
              Edit
            </button>
            <button
              onClick={handleDeleteQuestion}
              className="btn btn-danger btn-sm"
              disabled={deleting}
            >
              {deleting ? 'Deleting...' : 'Delete'}
            </button>
          </div>
        </div>
        {question.description && <p>{question.description}</p>}
        <div className="question-overview">
          <div className="overview-header">
            <span className="overview-muted">&nbsp;</span>
          </div>

          <div className="overview-stats">
            <div className="overview-stat">
              <span className="stat-label">Variables</span>
              <span className="stat-value">{totalVariables}</span>
              <span className="stat-sub">{depVariables.length} dependent, {indepVariables.length} independent</span>
            </div>
            <div className="overview-stat">
              <span className="stat-label">Data points</span>
              <span className="stat-value">{dataPointCount}</span>
              <span className="stat-sub">{dataPointCount === 1 ? 'entry' : 'entries'}</span>
            </div>
            <div className="overview-stat">
              <span className="stat-label">Last recorded</span>
              <span className="stat-value">
                {lastRecordedAt ? formatShortDate(lastRecordedAt) : '—'}
              </span>
              <span className="stat-sub">
                {lastRecordedAt ? new Date(lastRecordedAt).toLocaleTimeString() : 'No data points yet'}
              </span>
            </div>
            <div className="overview-stat">
              <span className="stat-label">Correlation</span>
              <span className={`stat-value ${hasCorrelations ? 'ok' : 'pending'}`}>
                {hasCorrelations ? 'Ready' : allCorrelationsFailed ? 'Not enough data' : correlationReady ? 'Computing' : 'Not ready'}
              </span>
              <span className="stat-sub">
                {hasCorrelations
                  ? 'Results available'
                  : allCorrelationsFailed
                    ? 'Add more data points'
                    : correlationReady
                      ? 'Waiting for computation'
                      : 'Add variables and data'}
              </span>
            </div>
          </div>

          {!totalVariables && (
            <div className="overview-cta">
              <p>No variables yet. Add at least one dependent and one independent variable to unlock correlations.</p>
              <button className="btn btn-primary btn-sm" onClick={() => setActiveTab('variables')}>
                Add variables
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="tabs">
        <button
          className={activeTab === 'data' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('data')}
          disabled={variables.length === 0}
        >
          Data Points
        </button>
        <button
          className={activeTab === 'variables' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('variables')}
        >
          Variables
        </button>
        <button
          className={activeTab === 'correlation' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('correlation')}
          disabled={depVariables.length === 0 || indepVariables.length === 0}
        >
          Correlation
        </button>
      </div>

      <div className="tab-content">
        {activeTab === 'variables' && (
          <VariableManager
            questionId={id!}
            variables={variables}
            onVariableAdded={loadQuestion}
          />
        )}
        {activeTab === 'data' && (
          <DataPointManager
            questionId={id!}
            variables={variables}
            dataPoints={sortedDataPoints}
            pagination={datasetPagination ?? {
              page: datasetPage,
              per_page: 50,
              total_count: sortedDataPoints.length,
              total_pages: 1,
            }}
            onPageChange={loadDataset}
            onDataChanged={() => loadDataset(1)}
          />
        )}
        {activeTab === 'correlation' && (
          <CorrelationView
            dependentVariables={depVariables}
            independentVariables={indepVariables}
            correlations={dataset?.correlations}
          />
        )}
      </div>

      {showEditModal && (
        <EditQuestionModal
          question={question}
          onClose={() => setShowEditModal(false)}
          onUpdate={handleUpdateQuestion}
        />
      )}
    </div>
  );
};

export default QuestionDetail;
